import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, CardContent, Rating, Stack, Typography } from '@mui/material';
import {
  Phone as PhoneIcon,
  Language as LanguageIcon,
  MailOutline as MailIcon,
} from '@mui/icons-material';
import { categoryLook, listingMeta } from '../../constants/directory';
import { clampedToLines } from '../../constants/layout';

/**
 * One listing in the directory results.
 *
 * Two deliberate changes from the version this replaces. The card is no
 * longer one big link: a phone number that is only reachable by opening the
 * listing first is a detour, and for a good part of the audience ringing up
 * *is* the errand — so the number is shown in full, in bold, and "Voir la
 * fiche" is its own named button. And the vignette gives the eye something to
 * recognise before it reads, which matters in a grid of businesses that have
 * no logo.
 *
 * Every band is the same height on every card of a row — the vignette, the
 * name, what it is and where, the rating, the description, the way to reach
 * them — so the whole row lines up and "Voir la fiche" sits on one line across
 * it. A band with nothing in it keeps its room rather than closing up, which
 * is what would pull everything below it out of line on that one card.
 */
function ProviderCard({ provider }) {
  const { contact, rating } = provider;
  const look = categoryLook(provider.category);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        aria-hidden="true"
        sx={{
          height: 108,
          bgcolor: look.tint,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {React.cloneElement(look.icon, { sx: { fontSize: 46, color: look.ink } })}
      </Box>

      <CardContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1, p: 2.75, pb: 2.75 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          <Typography variant="h4" component="h3" title={provider.name} sx={clampedToLines(2, 1.2)}>
            {provider.name}
          </Typography>
          <Typography variant="body2" noWrap>
            {listingMeta(provider.category, provider.neighborhood)}
            {/* Said plainly: "Entreprise" and "Particulier" do not read the
                same way when you are choosing who to let into your home. */}
            {'INDIVIDUAL' === provider.legal_status && (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {' · Particulier'}
              </Box>
            )}
          </Typography>
        </Box>

        {/* No reviews is not a bad rating: an unrated listing says so in
            words rather than showing five empty stars — and keeps the line,
            so its neighbours' stars stay level with its own. */}
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minHeight: 20 }}>
          {rating && rating.count > 0 ? (
            <>
              <Rating value={rating.average} precision={0.1} readOnly size="small" />
              <Typography variant="caption">
                {rating.average.toFixed(1).replace('.', ',')} sur 5 · {rating.count} avis
              </Typography>
            </>
          ) : (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Pas encore d’avis
            </Typography>
          )}
        </Stack>

        <Typography
          variant="body2"
          title={provider.description || undefined}
          sx={{ flexGrow: 1, ...clampedToLines(3, 1.55) }}
        >
          {provider.description}
        </Typography>

        {/* One way to be reached, in the order somebody would use it. A
            published listing always has one of the three, and the line is
            kept either way so the button below it never moves. */}
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, minWidth: 0, minHeight: 26 }}>
          {contact.phone_formatted && (
            <>
              <PhoneIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {contact.phone_formatted}
              </Typography>
            </>
          )}

          {!contact.phone_formatted && contact.website && (
            <>
              <LanguageIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body1" noWrap>
                {contact.website.replace(/^https?:\/\//, '')}
              </Typography>
            </>
          )}

          {!contact.phone_formatted && !contact.website && contact.email && (
            <>
              <MailIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body1" noWrap>
                {contact.email}
              </Typography>
            </>
          )}
        </Stack>

        <Button
          component={RouterLink}
          to={`/prestataires/${provider.slug}`}
          variant="contained"
          fullWidth
          // The listing's name is repeated for anyone tabbing through: a page
          // of buttons all called "Voir la fiche" tells them nothing.
          aria-label={`Voir la fiche de ${provider.name}`}
        >
          Voir la fiche
        </Button>
      </CardContent>
    </Card>
  );
}

export default ProviderCard;
